import { Component, OnInit, inject } from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { FormControl } from '@angular/forms';
import { firstValueFrom, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { InteractionService } from '../../service/interaction/interaction.service';
import { ActivatedRoute, Router } from '@angular/router';
import { TextToSpeechService } from '../../service/text-to-speech/text-to-speech.service';
import { ChatService } from '../../service/chat-service';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { MatOptionSelectionChange } from '@angular/material/core';
import { EncryptionService } from '../../service/encryption/encryption.service';
import { UserProfile } from '../../model/rolePlay.model';
import { MaintenanceDialogService } from '../../service/shared/maintenance-dialog.service';
import { DefaultBUPayload, PlatformRegionService } from '../../service/shared/platform-region.service';
import { Market, ProductPlatform, Region } from '../../model/shared/platform-region.model';
import { Role } from '../../model/shared/user.model';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'app-product-list',
    templateUrl: './product-list.component.html',
    styleUrl: './product-list.component.scss',
    providers: [
        {
            provide: MAT_SELECT_CONFIG,
            useValue: { overlayPanelClass: 'custom-multi-select-panel' },
        },
    ],
    standalone: false
})
export class ProductListComponent implements OnInit {

  productList = [];
  isChecked;
  userId: number = 0;
  userProfile!: UserProfile;
  @ViewChild('searchRegion') regionSearchTextBox: ElementRef;
  regionSelectFormControl = new FormControl();
  regionSearchTextboxControl = new FormControl();
  regionSelectedValues = '';
  regionData: string[] = [];
  regionFilteredOptions: Observable<any[]>;
  isRegionListDisabled: boolean = true;
  @ViewChild('searchMarket') marketSearchTextBox: ElementRef;
  marketSelectFormControl = new FormControl();
  marketSearchTextboxControl = new FormControl();
  marketSelectedValues = [];
  marketFilteredOptions: Observable<any[]>;
  isMarketListDisabled: boolean = true;
  selectedRegionsString: string;
  marketValues: string;
  isDoubleClickOnProduct: boolean = false;
  constructor(private readonly intService: InteractionService, private readonly router: Router,
    private readonly textToSpeechService: TextToSpeechService, private encryptionService: EncryptionService,
    private readonly chatService: ChatService, private route: ActivatedRoute,
    private maintenanceDialogService: MaintenanceDialogService,
    private readonly platformRegionService: PlatformRegionService,
    private toastrService: ToastrService
  ) {
  }

  ngOnInit() {
    this.userId = Number(sessionStorage.getItem('userId'));
    this.maintenanceDialogService.checkScheduleOutage().then((isScheduleOutage: boolean) => {
      if (!isScheduleOutage) {
        this.loadProducts()
      }
    });

    this.platformRegionService.marketSubject$.subscribe(() => {
      this.loadMarket();
    })
  }

  onSingleClick(item: any): void {
    item._clickTimer = setTimeout(() => {
      if (this.isDoubleClickOnProduct) {
        this.isDoubleClickOnProduct = false;
        const isSamePlatformClicked = this.platformRegionService.selectedProductPlatform.business_unit_code == item.business_unit_code;
        if (isSamePlatformClicked) {
          this.onProductDoubleClick();
        } else {
          this.toastrService.info('Platform is not selected.');
        }
      } else {
        this.selectProduct(item)
      }
    }, 500);
  }

  onDoubleClick(item: any): void {
    this.isDoubleClickOnProduct = true;
    clearTimeout(item._clickTimer);
  }

  get selectedProductPlatform() {
    return this.platformRegionService.selectedProductPlatform;
  }

  loadProducts() {
    this.productList = this.platformRegionService.productPlatformList;

    if (this.platformRegionService.selectedProductPlatform) {
      this.selectProduct(this.platformRegionService.selectedProductPlatform);
    }

    this.regionFilteredOptions = this.regionSearchTextboxControl.valueChanges
      .pipe(
        startWith<string>(''),
        map(name => this._filterRegion(name))
      );
    this.marketFilteredOptions = this.marketSearchTextboxControl.valueChanges
      .pipe(
        startWith<string>(''),
        map(name => this._filterMarket(name))
      );
  }

  onProductDoubleClick() {
    if (!this.platformRegionService.selected_platform_code) {
      this.toastrService.info('Platform is not selected.');
      return;
    }

    if (!this.platformRegionService.selected_region_code) {
      this.toastrService.info('Region is not selected.');
      return;
    }

    if (!this.platformRegionService.selected_market_code) {
      this.toastrService.info('Market is not selected.');
      return;
    }

    this.save();
  }

  selectProduct(product: ProductPlatform) {
    this.resetMarketData();
    this.platformRegionService.setSelectedProductPlatform(product)
    this.loadRegions();
  }

  resetRegionData() {
    this.regionData = [];
    this.isRegionListDisabled = false;
    this.regionSelectFormControl.enable();
    this.regionSelectedValues = '';
    this.regionSelectFormControl.setValue('');
    this.selectedRegionsString = this.regionSelectedValues;
  }

  loadRegions() {
    this.resetRegionData();
    this.regionData = this.platformRegionService.regionsList.map(region => region.label || region.name);
    this.isRegionListDisabled = this.regionData && this.regionData.length == 1;
    this.updateRegion();

    this.regionFilteredOptions = this.regionSearchTextboxControl.valueChanges
      .pipe(
        startWith<string>(''),
        map(name => this._filterRegion(name))
      );
  }

  updateRegion() {
    this.setRegionOnUI();
    this.resetMarketData();
  }

  setRegionOnUI() {
    this.regionSelectFormControl.setValue(this.platformRegionService.selected_region_label || this.platformRegionService.selected_region_code);
    this.regionSelectedValues = this.platformRegionService.selected_region_label || this.platformRegionService.selected_region_code;
    this.selectedRegionsString = this.platformRegionService.selected_region_code;

    if (this.isRegionListDisabled) {
      this.regionSelectFormControl.disable();
    }
  }

  loadMarket() {

    this.isMarketListDisabled = this.platformRegionService.marketList?.length === 1;
    this.marketSelectedValues = this.platformRegionService.selected_market_code.split(',');
    this.setMarketOnUI();

    this.marketFilteredOptions = this.marketSearchTextboxControl.valueChanges
      .pipe(
        startWith<string>(''),
        map((name: any) => this._filterMarket(name))
      );
  }

  setMarketOnUI() {
    this.marketSelectFormControl.setValue(this.marketSelectedValues);
    if (this.isMarketListDisabled) {
      this.marketSelectFormControl.disable();
    }
  }

  save() {
    this.setDefaultBU();
    this.setInitialPage();
  }

  setDefaultBU() {
    if (this.isChecked) {
      const market_selected = this.marketSelectFormControl.value.join(',');
      let payload: DefaultBUPayload = {
        wwid: this.userId,
        business_unit_code: this.selectedProductPlatform.business_unit_code,
        default_business_unit_code: this.selectedProductPlatform.business_unit_code,
        default_primary_region: this.selectedRegionsString ? this.selectedRegionsString : '',
        default_primary_market: market_selected,
        is_default_bu: true
      }

      this.chatService.updateDefaultBUMarket(payload).subscribe((response) => {
        this.platformRegionService.setDefaultPlatformCode(payload.business_unit_code, payload.default_primary_region, payload.default_primary_market);
        this.setDefaultDataInSession(payload)
      });
    }
  }

  private setInitialPage() {
    this.setSelectedProductDataInSession();
    this.setRoleDataInSession();
    this.textToSpeechService.initializeTTS();
    this.intService.isProductSelected.next(true);
    this.router.navigate(['/app-abiomed-chat'], { skipLocationChange: true });
  }

  setSelectedProductDataInSession() {
    sessionStorage.setItem('businessUnitCode', this.selectedProductPlatform.business_unit_code);
    sessionStorage.setItem('businessUnit', this.selectedProductPlatform.business_unit);
  }

  setDefaultDataInSession(payload: DefaultBUPayload) {
    sessionStorage.setItem('defaultProduct', payload.business_unit_code);
    sessionStorage.setItem('defaultRegion', payload.default_primary_market);
    sessionStorage.setItem('defaultMarket', payload.default_primary_region);
  }

  setRoleDataInSession() {
    const role: Role = this.platformRegionService.selectedRole;
    sessionStorage.setItem('displayRole', role.role_label); // Role Lable
    sessionStorage.setItem('role', role.role_name); // Role Name
    sessionStorage.setItem('roleCode', role.role_code); // Role code
  }

  private _filterRegion(name: string): String[] {
    const filterValue = name.toLowerCase();
    // Set selected values to retain the selected checkbox state 
    this.regionSetSelectedValues();
    this.regionSelectFormControl.patchValue(this.regionSelectedValues);
    let filteredList = this.regionData.filter(option => option.toLowerCase().indexOf(filterValue) === 0);
    return filteredList;
  }

  regionSelectionChange(event: MatOptionSelectionChange, option: string) {
    if (event.isUserInput) {
      if (event.source.selected) {
        this.platformRegionService.setDataForSelectedRegion(option);
        this.updateRegion();
      }
      this.selectedRegionsString = this.platformRegionService.selected_region_code;
    }
  }

  ///here do that
  regionOpenedChange(e) {
    this.regionSearchTextboxControl.patchValue('');
    if (e == true) {
      this.regionSearchTextBox.nativeElement.focus();
    } else {
      if (this.regionSelectFormControl.value.length && this.platformRegionService.selected_region_label != this.regionSelectFormControl.value) {
        this.resetMarketData();
        this.loadMarket();
      }
    }
  }

  regionClearSearch(event) {
    event.stopPropagation();
    this.regionSearchTextboxControl.patchValue('');
  }

  regionSetSelectedValues() {
    this.regionSelectedValues = this.regionSelectFormControl.value;
  }

  resetMarketData() {
    this.marketSelectedValues = [];
    this.marketSelectFormControl.setValue([]);
    this.marketSelectFormControl.enable();
  }

  private _filterMarket(name: string): Market[] {
    const filterValue = name.toLowerCase();
    // Set selected values to retain the selected checkbox state 
    this.marketSetSelectedValues();
    this.marketSelectFormControl.patchValue(this.marketSelectedValues);
    let filteredList = this.platformRegionService.marketList.filter((option: any) => option.primary_market.name?.toLowerCase().indexOf(filterValue) === 0);
    return filteredList;
  }

  marketSelectionChange(event) {
    if (event.isUserInput && event.source.selected == false) {
      let index = this.marketSelectedValues.indexOf(event.source.value);
      this.marketSelectedValues.splice(index, 1)
      this.platformRegionService.selected_market_code = this.marketSelectedValues.join(',');
    }
  }

  marketOpenedChange(e) {
    this.marketSearchTextboxControl.patchValue('');
    const selectedValues = this.marketSelectFormControl.value;
    this.marketValues = selectedValues.join(',');
    if (e == true) {
      this.marketSearchTextBox.nativeElement.focus();
    }
  }

  marketClearSearch(event) {
    event.stopPropagation();
    this.marketSearchTextboxControl.patchValue('');
  }

  marketSetSelectedValues() {
    if (this.marketSelectFormControl.value && this.marketSelectFormControl.value.length > 0) {
      this.marketSelectFormControl.value.forEach((e) => {
        if (this.marketSelectedValues.indexOf(e) == -1) {
          this.marketSelectedValues.push(e);
          this.platformRegionService.selected_market_code = this.marketSelectedValues.join(',');
        }
      });
    }
  }

  getMarketNameByCode(code) {
    let filteredVal: any;
    if (typeof (code) == 'object') {
      let codes = [];
      code.forEach(cd => {
        this.platformRegionService.marketList.forEach((market: any) => {
          if (market.primary_market.code.toString() === cd.toString()) {
            codes.push(market.primary_market.name);
          }
        });
      });
      filteredVal = codes
    } else if (typeof (code) == 'string') {
      let filteredName: any = this.platformRegionService.marketList.find((market: any) => market.primary_market.code.toString() === code.toString());
      filteredVal = filteredName?.primary_market.name
    }

    return filteredVal
  }
}
